import { Fragment } from "react";
import { Slider, SliderTrack, SliderThumb } from "react-aria-components";
import type { SliderProps } from "react-aria-components";
import { cx, sva } from "../../../../styled-system/css";

interface Mark {
  value: number;
  label?: string;
}

interface SliderWithMarksProps<T extends number | number[] = number | number[]> extends Omit<
  SliderProps<T>,
  "children" | "className"
> {
  marks: Mark[];
  className?: string;
}

const sliderRecipe = sva({
  slots: ["root", "track", "fill", "thumb", "markDot", "markLabel"],
  base: {
    root: {
      width: "100%",
      paddingBottom: "30px",
      _disabled: { opacity: 0.5 },
    },
    track: {
      position: "relative",
      height: "10px",
      borderRadius: "24px",
      backgroundColor: "lightgrey",
      cursor: "pointer",
      "[data-disabled] &": { cursor: "not-allowed" },
    },
    fill: {
      position: "absolute",
      height: "100%",
      borderRadius: "full",
      backgroundColor: "trellisPalette.0",
    },
    thumb: {
      top: "50%",
      width: "22px",
      height: "22px",
      borderRadius: "full",
      border: "1px solid {colors.trellisPalette.0}",
      backgroundColor: "white",
      cursor: "grab",
      outline: "none",
      _focusVisible: {
        outline: "2px solid {colors.bootstrapBlue}",
        outlineOffset: "2px",
      },
      "&[data-dragging]": { cursor: "grabbing" },
      "[data-disabled] &": { cursor: "not-allowed" },
    },
    markDot: {
      position: "absolute",
      top: "0",
      width: "2px",
      height: "10px",
      backgroundColor: "white",
      borderRadius: "full",
      transform: "translateX(-50%)",
      pointerEvents: "none",
      zIndex: 1,
    },
    markLabel: {
      position: "absolute",
      top: "100%",
      marginTop: "14px",
      transform: "translateX(-50%)",
      pointerEvents: "none",
      fontSize: "11px",
      whiteSpace: "pre-wrap",
      overflowWrap: "break-word",
      textAlign: "center",
    },
  },
  variants: {
    hasNarrowGaps: {
      true: { root: { paddingBottom: "48px" } },
    },
  },
});

function computeLabelMaxWidths(marks: Mark[], minValue: number, maxValue: number) {
  const range = maxValue - minValue;
  if (range === 0) return new Map<number, number>();

  const labeledMarks = marks
    .filter((m) => m.label)
    .map((m) => ({
      value: m.value,
      percent: ((m.value - minValue) / range) * 100,
    }))
    .sort((a, b) => a.percent - b.percent);

  const maxWidths = new Map<number, number>();
  for (let i = 0; i < labeledMarks.length; i++) {
    const current = labeledMarks[i]!;
    let gap = 100;
    if (i > 0) gap = Math.min(gap, current.percent - labeledMarks[i - 1]!.percent);
    if (i < labeledMarks.length - 1) gap = Math.min(gap, labeledMarks[i + 1]!.percent - current.percent);
    maxWidths.set(current.value, gap);
  }

  return maxWidths;
}

export function SliderWithMarks<T extends number | number[] = number | number[]>({
  marks,
  className,
  minValue = 0,
  maxValue = 100,
  ...restProps
}: SliderWithMarksProps<T>) {
  const labelMaxWidths = computeLabelMaxWidths(marks, minValue, maxValue);
  const hasNarrowGaps = Array.from(labelMaxWidths.values()).some((gap) => gap < 15);
  const classes = sliderRecipe({ hasNarrowGaps });

  return (
    <Slider {...restProps} minValue={minValue} maxValue={maxValue} className={cx(classes.root, className)}>
      <SliderTrack className={classes.track}>
        {({ state }) => (
          <>
            <div className={classes.fill} style={{ width: `${state.getThumbPercent(0) * 100}%` }} />
            {marks.map((mark) => {
              const percent = state.getValuePercent(mark.value) * 100;
              return (
                <Fragment key={mark.value}>
                  <div className={classes.markDot} style={{ left: `${percent}%` }} />
                  {mark.label && (
                    <div
                      className={classes.markLabel}
                      style={{
                        left: `${percent}%`,
                        maxWidth: `${labelMaxWidths.get(mark.value) ?? 100}%`,
                      }}
                    >
                      {mark.label}
                    </div>
                  )}
                </Fragment>
              );
            })}
            <SliderThumb className={classes.thumb} />
          </>
        )}
      </SliderTrack>
    </Slider>
  );
}
